// src/app/components/prompt-tracking/edit-prompt-modal.tsx

import { useState, useEffect } from 'react';
import { X, Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  INTENT_CONFIG,
  AVAILABLE_MODELS,
} from '../../constants/prompt-tracking-constants';
import { updatePrompt } from '../../services/prompt-tracking/prompt-tracking-api';
import { usePromptAnalysis } from '../../hooks/prompt-tracking/usePromptAnalysis';
import type { Prompt, PromptIntent } from '../../types/prompt-tracking-types';

interface EditPromptModalProps {
  prompt: Prompt;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
  brandName: string;
}

type Step = 'form' | 'analyzing' | 'done' | 'error';

export function EditPromptModal({
  prompt,
  isOpen,
  onClose,
  onUpdated,
  brandName,
}: EditPromptModalProps) {
  const [text, setText] = useState(prompt.text);
  const [intent, setIntent] = useState<PromptIntent>(prompt.intent);
  const [selectedModels, setSelectedModels] = useState<string[]>(prompt.models);
  const [step, setStep] = useState<Step>('form');
  const [saveError, setSaveError] = useState<string | null>(null);

  const { status, progress, errorMessage, runAnalysis, reset: resetAnalysis } = usePromptAnalysis();

  useEffect(() => {
    if (isOpen) {
      setText(prompt.text);
      setIntent(prompt.intent);
      setSelectedModels(prompt.models);
      setStep('form');
      setSaveError(null);
      resetAnalysis();
    }
  }, [isOpen, prompt.id]);

  useEffect(() => {
    if (step === 'analyzing') {
      if (status === 'done') setStep('done');
      else if (status === 'error') setStep('error');
    }
  }, [status, step]);

  if (!isOpen) return null;

  const toggleModel = (id: string) =>
    setSelectedModels((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );

  const handleSave = async () => {
    if (!text.trim()) { setSaveError('Prompt text cannot be empty.'); return; }
    if (selectedModels.length === 0) { setSaveError('Select at least one model.'); return; }
    setSaveError(null);
    setStep('analyzing');

    try {
      await updatePrompt(prompt.id, {
        text:   text.trim(),
        intent,
        models: selectedModels,
      });

      runAnalysis({
        promptId:   prompt.id,
        promptText: text.trim(),
        intent,
        brandName,
        models:     selectedModels,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes.');
      setStep('form');
    }
  };

  const handleDone = () => {
    onUpdated();
    onClose();
  };

  const handleClose = () => {
    if (step === 'analyzing') return;
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="w-full rounded-2xl shadow-2xl flex flex-col"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border)',
          maxHeight: '90vh',
          maxWidth: 'min(520px, calc(100dvw - 24px))',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 md:py-5 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--signal-blue-10)' }}
            >
              <Sparkles className="w-4 h-4" style={{ color: 'var(--signal-blue)' }} />
            </div>
            <div className="min-w-0">
              <h2
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {step === 'form'
                  ? 'Edit Prompt'
                  : step === 'analyzing'
                  ? 'Re-running Analysis…'
                  : step === 'done'
                  ? 'Analysis Updated'
                  : 'Analysis Failed'}
              </h2>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {step === 'form'
                  ? 'Changes will trigger a fresh analysis run'
                  : step === 'analyzing'
                  ? `Running across ${selectedModels.length} models`
                  : step === 'done'
                  ? 'Updated results saved successfully'
                  : 'Changes saved — retry analysis manually'}
              </p>
            </div>
          </div>
          {step !== 'analyzing' && (
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0"
              style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}
            >
              <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 md:py-6">

          {step === 'form' && (
            <div className="space-y-5 md:space-y-6">
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                    marginBottom: '8px',
                  }}
                >
                  Prompt text
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  className="w-full p-3 rounded-xl resize-none"
                  style={{
                    backgroundColor: 'var(--muted)',
                    border: '1.5px solid var(--border)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: '1.6',
                    color: 'var(--foreground)',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--signal-blue)')}
                  onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                    marginBottom: '8px',
                  }}
                >
                  Intent
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {INTENT_CONFIG.map((cfg) => {
                    const Icon = cfg.icon;
                    const isSelected = intent === cfg.key;
                    return (
                      <button
                        key={cfg.key}
                        onClick={() => setIntent(cfg.key)}
                        className="flex items-start gap-3 p-3 rounded-xl transition-all text-left"
                        style={{
                          backgroundColor: isSelected ? cfg.bgColor : 'var(--muted)',
                          border: isSelected
                            ? `1.5px solid ${cfg.color}`
                            : '1.5px solid var(--border)',
                          cursor: 'pointer',
                        }}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: isSelected ? cfg.color + '20' : 'var(--background)',
                          }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                        </div>
                        <div className="min-w-0">
                          <div
                            style={{
                              fontSize: 'var(--text-sm)',
                              fontWeight: 'var(--font-weight-semibold)',
                              color: isSelected ? cfg.color : 'var(--foreground)',
                              marginBottom: '2px',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {cfg.shortLabel}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--muted-foreground)',
                              lineHeight: '1.4',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {cfg.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                    marginBottom: '4px',
                  }}
                >
                  AI Models
                </label>
                <p
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                    marginBottom: '10px',
                  }}
                >
                  Saving will re-run analysis against all selected models.
                </p>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_MODELS.map((model) => {
                    const isSel = selectedModels.includes(model.id);
                    return (
                      <button
                        key={model.id}
                        onClick={() => toggleModel(model.id)}
                        className="flex-1 min-w-[100px] py-2 px-3 rounded-xl transition-all"
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          backgroundColor: isSel ? 'var(--signal-blue-10)' : 'var(--muted)',
                          border: isSel
                            ? '1.5px solid var(--signal-blue)'
                            : '1.5px solid var(--border)',
                          color: isSel ? 'var(--signal-blue)' : 'var(--muted-foreground)',
                          cursor: 'pointer',
                        }}
                      >
                        {model.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {saveError && (
                <p
                  className="px-3 py-2 rounded-lg"
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--infrared-coral)',
                    backgroundColor: 'var(--infrared-coral-10)',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {saveError}
                </p>
              )}
            </div>
          )}

          {step === 'analyzing' && (
            <div className="py-4 space-y-5 md:space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                    {status === 'connecting' ? 'Connecting…' : 'Re-analyzing across models…'}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--signal-blue)',
                    }}
                  >
                    {progress ? `${progress.done}/${progress.total}` : ''}
                  </span>
                </div>
                <div
                  className="w-full h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--muted)' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progress?.percent ?? 0}%`,
                      backgroundColor: 'var(--signal-blue)',
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                {AVAILABLE_MODELS.filter((m) => selectedModels.includes(m.id)).map((model, i) => {
                  const done    = progress ? i < progress.done : false;
                  const running = progress ? i === progress.done : false;
                  return (
                    <div
                      key={model.id}
                      className="flex items-center gap-3 p-3 rounded-lg"
                      style={{ backgroundColor: 'var(--muted)' }}
                    >
                      {done ? (
                        <CheckCircle2
                          className="w-4 h-4 flex-shrink-0"
                          style={{ color: 'var(--success-text)' }}
                        />
                      ) : running ? (
                        <Loader2
                          className="w-4 h-4 animate-spin flex-shrink-0"
                          style={{ color: 'var(--signal-blue)' }}
                        />
                      ) : (
                        <div
                          className="w-4 h-4 rounded-full border-2 flex-shrink-0"
                          style={{ borderColor: 'var(--border)' }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: 'var(--text-sm)',
                          color: done ? 'var(--foreground)' : 'var(--muted-foreground)',
                          fontWeight: done ? 'var(--font-weight-semibold)' : 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        }}
                      >
                        {model.name}
                      </span>
                      {done    && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--success-text)', marginLeft: 'auto' }}>Done</span>}
                      {running && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--signal-blue)', marginLeft: 'auto' }}>Running…</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="py-6 md:py-8 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--success-text)' }} />
              <h3
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: '8px',
                }}
              >
                Changes saved & re-analyzed
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                Updated results from {selectedModels.length} models have been stored.
              </p>
            </div>
          )}

          {step === 'error' && (
            <div className="py-6 md:py-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--infrared-coral)' }} />
              <h3
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: '8px',
                }}
              >
                Analysis failed
              </h3>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {errorMessage || 'Something went wrong.'} Changes were saved — use Run Analysis to retry.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-3 px-4 md:px-6 py-4 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!text.trim() || selectedModels.length === 0}
                className="w-full sm:w-auto"
                style={{
                  backgroundColor: 'var(--signal-blue)',
                  color: 'white',
                  border: 'none',
                  opacity: !text.trim() || selectedModels.length === 0 ? 0.6 : 1,
                }}
              >
                Save & Re-run Analysis
              </Button>
            </>
          )}

          {step === 'analyzing' && (
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                textAlign: 'center',
              }}
            >
              Please wait…
            </p>
          )}

          {(step === 'done' || step === 'error') && (
            <Button
              onClick={handleDone}
              className="w-full sm:w-auto"
              style={{ backgroundColor: 'var(--signal-blue)', color: 'white', border: 'none' }}
            >
              {step === 'done' ? 'View Results' : 'Close'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}